import React, { useState } from 'react';
import { seminarService, attendanceService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import AttendanceModal from '../components/admin/AttendanceModal';
import {
  Plus, Edit2, Trash2, Copy, CheckCircle2, Eye, EyeOff,
  Calendar, Clock, Users, Video, Star, Loader2, X,
  AlertCircle, Search, ToggleLeft, ToggleRight, ClipboardList,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Seminar Form Modal ────────────────────────────────────────
const SeminarModal = ({ existing, onSave, onClose }) => {
  const [form, setForm] = useState({
    title:        existing?.title        ?? '',
    description:  existing?.description  ?? '',
    date:         existing?.date         ? new Date(existing.date).toISOString().slice(0,16) : '',
    endDate:      existing?.endDate      ? new Date(existing.endDate).toISOString().slice(0,16) : '',
    promoVideoUrl: existing?.promoVideoUrl ?? '',
    imageUrl:     existing?.imageUrl     ?? '',
    hostName:     existing?.hostName     ?? '',
    hostTitle:    existing?.hostTitle    ?? '',
    location:     existing?.location     ?? 'Online',
    maxAttendees: existing?.maxAttendees ?? '',
    isPublished:  existing?.isPublished  ?? true,
  });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Title and date are required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save seminar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-semibold text-forest-900">
            {existing ? 'Edit Seminar' : 'Create Seminar'}
          </h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        {[
          { label: 'Title *', key: 'title', placeholder: 'e.g. Tourism Innovation Summit 2026' },
          { label: 'Host Name', key: 'hostName', placeholder: 'e.g. Dr. Kwame Asante' },
          { label: 'Host Title', key: 'hostTitle', placeholder: 'e.g. CEO, TourEnt Hub' },
          { label: 'Location', key: 'location', placeholder: 'Online / Accra, Ghana' },
          { label: 'Promo Video URL', key: 'promoVideoUrl', placeholder: 'https://youtube.com/...' },
          { label: 'Banner Image URL', key: 'imageUrl', placeholder: 'https://...' },
          { label: 'Max Attendees', key: 'maxAttendees', placeholder: 'Leave blank for unlimited' },
        ].map(({ label, key, placeholder }) => (
          <div key={key}>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">{label}</label>
            <input value={form[key]} onChange={f(key)} placeholder={placeholder}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
          </div>
        ))}

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Description</label>
          <textarea value={form.description} onChange={f('description')} rows={3}
            placeholder="What is this seminar about?"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">Start Date & Time *</label>
            <input type="datetime-local" value={form.date} onChange={f('date')}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">End Date & Time</label>
            <input type="datetime-local" value={form.endDate} onChange={f('endDate')}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
          </div>
        </div>

        <div className="flex items-center justify-between py-2 border-t border-cream-100">
          <div>
            <p className="text-sm font-medium text-forest-900">Published</p>
            <p className="text-xs text-forest-400">Show on landing page carousel</p>
          </div>
          <button onClick={() => setForm(p => ({ ...p, isPublished: !p.isPublished }))}>
            {form.isPublished
              ? <ToggleRight size={32} className="text-forest-600" />
              : <ToggleLeft  size={32} className="text-forest-400" />}
          </button>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="btn-primary w-full justify-center flex items-center gap-2 disabled:opacity-60">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : 'Save Seminar'}
        </button>
      </div>
    </div>
  );
};

// ── Featured Profile Row ──────────────────────────────────────
const FeaturedRow = ({ profile, onRemove }) => {
  const name = `${profile.user?.firstName ?? ''} ${profile.user?.lastName ?? ''}`.trim();
  const sub  = profile.type === 'EXPERT'
    ? profile.user?.expertProfile?.specializations
    : profile.user?.clientProfile?.businessName;
  const specs = (() => { try { return Array.isArray(sub) ? sub : JSON.parse(sub || '[]'); } catch { return typeof sub === 'string' ? [sub] : []; } })();

  return (
    <div className="flex items-center gap-3 py-3 border-b border-cream-100 last:border-0">
      <div className="w-9 h-9 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm overflow-hidden flex-shrink-0">
        {profile.user?.avatar
          ? <img src={profile.user.avatar} alt="" className="w-full h-full object-cover" />
          : name[0]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-forest-900">{name}</p>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            profile.type === 'EXPERT' ? 'bg-amber-50 text-amber-700' : 'bg-forest-50 text-forest-600'
          }`}>{profile.type}</span>
          {specs.slice(0,2).map((s,i) => (
            <span key={i} className="text-[10px] text-forest-400">{s}</span>
          ))}
        </div>
      </div>
      <button onClick={() => onRemove(profile.user.id)}
        className="text-forest-300 hover:text-rust-500 transition-colors flex-shrink-0">
        <X size={14} />
      </button>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminSeminarsPage = () => {
  const [showModal,    setShowModal]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [copied,       setCopied]       = useState({});
  const [refetch,      setRefetch]      = useState(0);
  const [userSearch,   setUserSearch]   = useState('');
  const [userQuery,    setUserQuery]    = useState('');
  const [activeTab,    setActiveTab]    = useState('seminars');
  const [attendanceSeminar, setAttendanceSeminar] = useState(null);

  const refresh = () => setRefetch(r => r + 1);

  const { data: seminarsData, loading: loadingSeminars } = useApi(
    () => seminarService.getAll(), [refetch]
  );
  const { data: featuredData, loading: loadingFeatured } = useApi(
    () => seminarService.getFeatured(), [refetch]
  );
  const { data: usersData } = useApi(
    () => seminarService.searchUsers(userQuery), [userQuery]
  );

  const seminars  = seminarsData?.seminars ?? seminarsData?.data?.seminars ?? [];
  const featured  = featuredData?.profiles ?? featuredData?.data?.profiles ?? [];
  const users     = Array.isArray(usersData?.data?.users) ? usersData.data.users
    : Array.isArray(usersData?.users) ? usersData.users : [];

  const handleRegenerateRoom = async (id) => {
    try {
      await seminarService.regenerateRoom(id);
      toast.success('Session room created!');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create room — check your DAILY_API_KEY in .env');
    }
  };

  const handleCreate = async (form) => {
    await seminarService.create(form);
    toast.success('Seminar created!');
    refresh();
  };

  const handleUpdate = async (form) => {
    await seminarService.update(editing.id, form);
    toast.success('Seminar updated!');
    refresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this seminar?')) return;
    await seminarService.remove(id);
    toast.success('Seminar deleted');
    refresh();
  };

  const handleTogglePublish = async (seminar) => {
    await seminarService.update(seminar.id, { isPublished: !seminar.isPublished });
    toast.success(seminar.isPublished ? 'Unpublished' : 'Published');
    refresh();
  };

  const handleCopyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(p => ({ ...p, [id]: true }));
    toast.success('Join code copied!');
    setTimeout(() => setCopied(p => ({ ...p, [id]: false })), 2000);
  };

  const handleAddFeatured = async (userId, type) => {
    await seminarService.toggleFeatured(userId, { type });
    toast.success('Added to featured!');
    refresh();
  };

  const handleRemoveFeatured = async (userId) => {
    await seminarService.toggleFeatured(userId, {});
    toast.success('Removed from featured');
    refresh();
  };

  return (
    <DashboardLayout title="Seminars & Featured">
      <div className="max-w-4xl space-y-6">

        {/* Tabs */}
        <div className="flex gap-2 border-b border-cream-200">
          {[
            { id: 'seminars', label: 'Seminars & Programs' },
            { id: 'featured', label: 'Featured Profiles' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === t.id
                  ? 'border-forest-700 text-forest-900'
                  : 'border-transparent text-forest-400 hover:text-forest-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── SEMINARS TAB ── */}
        {activeTab === 'seminars' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-forest-500">Create upcoming seminars shown on the landing page carousel.</p>
              <button onClick={() => { setEditing(null); setShowModal(true); }}
                className="btn-primary flex items-center gap-2 text-sm">
                <Plus size={14} /> New Seminar
              </button>
            </div>

            {loadingSeminars ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-forest-600" />
              </div>
            ) : seminars.length === 0 ? (
              <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
                <Calendar size={36} className="mx-auto mb-3 opacity-30" />
                <p className="font-serif text-lg text-forest-600">No seminars yet</p>
                <p className="text-sm mt-1 mb-4">Create your first seminar to display on the landing page.</p>
                <button onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary inline-flex items-center gap-2">
                  <Plus size={14} /> Create Seminar
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {seminars.map(s => {
                  const date = new Date(s.date).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' });
                  return (
                    <div key={s.id} className="bg-white border border-cream-200 rounded-2xl p-5">
                      <div className="flex items-start gap-4">
                        {s.imageUrl ? (
                          <img src={s.imageUrl} alt={s.title}
                            className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border border-cream-200" />
                        ) : (
                          <div className="w-16 h-16 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Calendar size={24} className="text-forest-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <h4 className="font-serif font-semibold text-forest-900">{s.title}</h4>
                              <p className="text-xs text-forest-400 mt-0.5 flex items-center gap-1">
                                <Calendar size={10} /> {date} · {s.location}
                              </p>
                              {s.hostName && (
                                <p className="text-xs text-forest-500 mt-0.5">
                                  {s.hostName}{s.hostTitle ? ` — ${s.hostTitle}` : ''}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${
                                s.status === 'UPCOMING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                s.status === 'LIVE'     ? 'bg-green-50 text-green-700 border-green-200' :
                                'bg-cream-100 text-forest-400 border-cream-200'
                              }`}>{s.status}</span>
                              {!s.isPublished && (
                                <span className="text-[10px] font-semibold border px-2 py-0.5 rounded-full bg-cream-100 text-forest-400 border-cream-200">
                                  Draft
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Join code */}
                          <div className="flex items-center gap-2 mt-3 bg-forest-900 rounded-xl px-4 py-2.5 w-fit">
                            <span className="font-mono font-bold text-cream-50 tracking-widest text-sm">{s.joinCode}</span>
                            <button onClick={() => handleCopyCode(s.joinCode, s.id)}
                              className="text-forest-400 hover:text-cream-50 transition-colors ml-1">
                              {copied[s.id]
                                ? <CheckCircle2 size={14} className="text-green-400" />
                                : <Copy size={14} />}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">
                        <button onClick={() => handleTogglePublish(s)}
                          className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
                          {s.isPublished ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                        </button>
                        <button onClick={() => { setEditing(s); setShowModal(true); }}
                          className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
                          <Edit2 size={12} /> Edit
                        </button>
                        {!s.dailyRoomUrl && (
                          <button onClick={() => handleRegenerateRoom(s.id)}
                            className="flex items-center gap-1.5 text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
                            <Video size={12} /> Create Room
                          </button>
                        )}
                        <button onClick={() => setAttendanceSeminar(s)}
                          className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
                          <ClipboardList size={12} /> Attendance
                        </button>
                        <button onClick={() => handleDelete(s.id)}
                          className="flex items-center gap-1.5 text-xs text-rust-500 border border-rust-200 hover:bg-rust-50 px-3 py-1.5 rounded-full transition-colors font-medium ml-auto">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── FEATURED PROFILES TAB ── */}
        {activeTab === 'featured' && (
          <div className="space-y-5">
            <p className="text-sm text-forest-500">
              Select experts and mentees to feature on the landing page carousel.
            </p>

            {/* Search to add */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5 space-y-4">
              <h4 className="font-serif font-semibold text-forest-900">Add to Featured</h4>
              <form onSubmit={e => { e.preventDefault(); setUserQuery(userSearch.trim()); }}
                className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search experts or clients by name..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
                </div>
                <button type="submit" className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium">
                  Search
                </button>
              </form>

              {users.length > 0 && (
                <div className="space-y-2 mt-2">
                  {users.map(u => {
                    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                    const isExpert = u.role === 'EXPERT';
                    const isAlreadyFeatured = featured.some(f => f.userId === u.id || f.user?.id === u.id);
                    const sub = isExpert
                      ? (u.expertProfile?.specializations ? (() => { try { const s = u.expertProfile.specializations; return Array.isArray(s) ? s[0] : JSON.parse(s)?.[0]; } catch { return ''; } })() : '')
                      : (u.clientProfile?.businessName ?? '');
                    return (
                      <div key={u.id} className="flex items-center gap-3 py-2 border-b border-cream-100 last:border-0">
                        <div className="w-9 h-9 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm overflow-hidden flex-shrink-0">
                          {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-forest-900 truncate">{name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isExpert ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                            }`}>{isExpert ? 'Expert' : 'Client'}</span>
                            {sub && <span className="text-[10px] text-forest-400 truncate">{sub}</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddFeatured(u.id, isExpert ? 'EXPERT' : 'MENTEE')}
                          disabled={isAlreadyFeatured}
                          className="text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                          {isAlreadyFeatured ? '✓ Added' : '+ Feature'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {userQuery && users.length === 0 && (
                <p className="text-xs text-forest-400 italic mt-2">No users found for "{userQuery}"</p>
              )}
            </div>

            {/* Current featured list */}
            <div className="bg-white border border-cream-200 rounded-2xl p-5">
              <h4 className="font-serif font-semibold text-forest-900 mb-4">
                Currently Featured ({featured.length})
              </h4>
              {loadingFeatured ? (
                <Loader2 size={18} className="animate-spin text-forest-400" />
              ) : featured.length === 0 ? (
                <p className="text-sm text-forest-400 italic">No featured profiles yet. Search and add experts or mentees above.</p>
              ) : (
                featured.map(p => (
                  <FeaturedRow
                    key={p.id}
                    profile={p}
                    onRemove={handleRemoveFeatured}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <SeminarModal
          existing={editing}
          onSave={editing ? handleUpdate : handleCreate}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}

      {attendanceSeminar && (
        <AttendanceModal
          title={attendanceSeminar.title}
          roomLabel={new Date(attendanceSeminar.date).toLocaleDateString('en-GH', { dateStyle: 'long' })}
          fetchFn={() => attendanceService.getForSeminar(attendanceSeminar.id)}
          syncFn={() => attendanceService.syncSeminar(attendanceSeminar.id)}
          onClose={() => setAttendanceSeminar(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminSeminarsPage;